/**
 * Phishing Simulations Page
 * GitHub Issue #285
 *
 * Main admin view for managing phishing simulation campaigns.
 */

import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  api,
  PhishingTemplate,
  PhishingCampaign,
  PhishingDashboardStats,
  PhishingCampaignStatus,
  PhishingDifficulty,
  PhishingSenderProfile,
} from '../lib/api'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import { useAuth } from '../hooks/useAuth'
import {
  Fish,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Edit2,
  Trash2,
  X,
  Play,
  Pause,
  CheckCircle2,
  Clock,
  Users,
  Mail,
  MousePointerClick,
  AlertTriangle,
  Eye,
  Copy,
  Send,
  Calendar,
  BarChart3,
  Shield,
  TrendingUp,
  TrendingDown,
  FileText,
  Lock,
  Flag,
  Download,
  Sparkles,
  Target,
  Building2,
  RefreshCw,
  Loader2,
  UserX,
  Award,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'

// Tab types
type TabType = 'dashboard' | 'campaigns' | 'templates' | 'senders'

// Dashboard sub-tabs for analytics
type DashboardSubTab = 'overview' | 'trends' | 'templates' | 'departments' | 'offenders'

// Types for advanced analytics
interface PhishingTrendData {
  month: string
  emails_sent: number
  clicks: number
  submissions: number
  reports: number
  click_rate: number
  compromise_rate: number
  report_rate: number
}

interface EffectiveTemplate {
  template_id: string
  name: string
  category: string
  difficulty: PhishingDifficulty
  campaigns_used: number
  total_sent: number
  total_clicks: number
  click_rate: number
  compromise_rate: number
}

interface DepartmentVulnerability {
  role: string
  total_targets: number
  clicked: number
  compromised: number
  reported: number
  click_rate: number
  compromise_rate: number
  report_rate: number
}

interface RepeatOffender {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string | null
  total_campaigns: number
  times_clicked: number
  times_compromised: number
  times_reported: number
  click_rate: number
}

const statusColors: Record<PhishingCampaignStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-purple-100 text-purple-700',
  sending: 'bg-orange-100 text-orange-700',
  active: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
}

const difficultyColors: Record<PhishingDifficulty, string> = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-orange-100 text-orange-700',
  expert: 'bg-red-100 text-red-700',
}

const CHART_COLORS = ['#22c55e', '#3b82f6', '#eab308', '#ef4444']

export default function Phishing() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const confirmDialog = useConfirm()

  // State
  const [activeTab, setActiveTab] = useState<TabType>('dashboard')
  const [loading, setLoading] = useState(true)
  const [dashboardStats, setDashboardStats] = useState<PhishingDashboardStats | null>(null)
  const [campaigns, setCampaigns] = useState<PhishingCampaign[]>([])
  const [templates, setTemplates] = useState<PhishingTemplate[]>([])
  const [senderProfiles, setSenderProfiles] = useState<PhishingSenderProfile[]>([])
  const [campaignPagination, setCampaignPagination] = useState({ total: 0, limit: 20, offset: 0 })
  const [templatePagination, setTemplatePagination] = useState({ total: 0, limit: 20, offset: 0 })

  // Filters
  const [campaignSearch, setCampaignSearch] = useState('')
  const [campaignStatusFilter, setCampaignStatusFilter] = useState<PhishingCampaignStatus | ''>('')
  const [templateSearch, setTemplateSearch] = useState('')
  const [templateDifficultyFilter, setTemplateDifficultyFilter] = useState<PhishingDifficulty | ''>('')

  // Modals
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false)
  const [showCreateSenderModal, setShowCreateSenderModal] = useState(false)
  const [showTemplatePreview, setShowTemplatePreview] = useState<PhishingTemplate | null>(null)

  // Form data
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    template_id: '',
    target_type: 'all_users' as 'all_users' | 'specific_users' | 'by_role' | 'by_department',
    track_opens: true,
    track_clicks: true,
    capture_credentials: false,
    requires_approval: true,
  })

  const [senderForm, setSenderForm] = useState({
    name: '',
    from_name: '',
    from_email: '',
    reply_to_email: '',
  })

  // Advanced Analytics State (Phase 4)
  const [dashboardSubTab, setDashboardSubTab] = useState<DashboardSubTab>('overview')
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [phishingTrends, setPhishingTrends] = useState<PhishingTrendData[]>([])
  const [effectiveTemplates, setEffectiveTemplates] = useState<EffectiveTemplate[]>([])
  const [departmentVulnerability, setDepartmentVulnerability] = useState<DepartmentVulnerability[]>([])
  const [repeatOffenders, setRepeatOffenders] = useState<RepeatOffender[]>([])
  const [exportLoading, setExportLoading] = useState(false)

  // AI State
  const [aiGenerating, setAiGenerating] = useState(false)
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiSubject, setAiSubject] = useState('')
  const [aiSenderName, setAiSenderName] = useState('')
  const [generatedIndicators, setGeneratedIndicators] = useState<string[]>([])

  // Fetch data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.getPhishingDashboard()
      if (response.success) {
        setDashboardStats(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error)
    }
  }, [])

  const fetchCampaigns = useCallback(async () => {
    try {
      const params: any = { limit: 20, offset: campaignPagination.offset }
      if (campaignSearch) params.search = campaignSearch
      if (campaignStatusFilter) params.status = campaignStatusFilter

      const response = await api.getPhishingCampaigns(params)
      if (response.success) {
        setCampaigns(response.data)
        setCampaignPagination(response.pagination)
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error)
    }
  }, [campaignSearch, campaignStatusFilter, campaignPagination.offset])

  const fetchTemplates = useCallback(async () => {
    try {
      const params: any = { limit: 20, offset: templatePagination.offset }
      if (templateSearch) params.search = templateSearch
      if (templateDifficultyFilter) params.difficulty = templateDifficultyFilter

      const response = await api.getPhishingTemplates(params)
      if (response.success) {
        setTemplates(response.data)
        setTemplatePagination(response.pagination)
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error)
    }
  }, [templateSearch, templateDifficultyFilter, templatePagination.offset])

  const fetchSenderProfiles = useCallback(async () => {
    try {
      const response = await api.getPhishingSenderProfiles()
      if (response.success) {
        setSenderProfiles(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch sender profiles:', error)
    }
  }, [])

  // Advanced Analytics Fetch Functions (Phase 4)
  const fetchPhishingTrends = useCallback(async () => {
    try {
      const response = await api.getPhishingTrends(6)
      if (response.success) {
        setPhishingTrends(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch phishing trends:', error)
    }
  }, [])

  const fetchEffectiveTemplates = useCallback(async () => {
    try {
      const response = await api.getPhishingEffectiveTemplates(10)
      if (response.success) {
        setEffectiveTemplates(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch effective templates:', error)
    }
  }, [])

  const fetchDepartmentVulnerability = useCallback(async () => {
    try {
      const response = await api.getPhishingByDepartment()
      if (response.success) {
        setDepartmentVulnerability(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch department vulnerability:', error)
    }
  }, [])

  const fetchRepeatOffenders = useCallback(async () => {
    try {
      const response = await api.getPhishingRepeatOffenders({ limit: 20, min_clicks: 2 })
      if (response.success) {
        setRepeatOffenders(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch repeat offenders:', error)
    }
  }, [])

  // Load analytics data based on sub-tab
  const loadAnalyticsData = useCallback(async (subTab: DashboardSubTab) => {
    if (subTab === 'overview') return
    setAnalyticsLoading(true)
    try {
      switch (subTab) {
        case 'trends':
          await fetchPhishingTrends()
          break
        case 'templates':
          await fetchEffectiveTemplates()
          break
        case 'departments':
          await fetchDepartmentVulnerability()
          break
        case 'offenders':
          await fetchRepeatOffenders()
          break
      }
    } finally {
      setAnalyticsLoading(false)
    }
  }, [fetchPhishingTrends, fetchEffectiveTemplates, fetchDepartmentVulnerability, fetchRepeatOffenders])

  // Export handler
  const handleExportReport = async (type: 'results' | 'campaigns') => {
    setExportLoading(true)
    try {
      const blob = await api.exportPhishingReport({ format: 'csv', type }) as Blob
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `phishing-${type}-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Report exported successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to export report')
    } finally {
      setExportLoading(false)
    }
  }

  // AI Indicator Suggestion
  const handleSuggestIndicators = async () => {
    if (!aiSubject.trim()) {
      toast.error('Please enter a subject line')
      return
    }
    setAiGenerating(true)
    setGeneratedIndicators([])
    try {
      const response = await api.suggestPhishingIndicators({
        subject: aiSubject,
        sender_name: aiSenderName || undefined,
      })
      if (response.success) {
        setGeneratedIndicators(response.data.indicators)
        toast.success(`Generated ${response.data.indicators.length} indicators`)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to suggest indicators')
    } finally {
      setAiGenerating(false)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchDashboard(), fetchSenderProfiles()])
      setLoading(false)
    }
    loadData()
  }, [fetchDashboard, fetchSenderProfiles])

  useEffect(() => {
    if (activeTab === 'campaigns') {
      fetchCampaigns()
    } else if (activeTab === 'templates') {
      fetchTemplates()
    }
  }, [activeTab, fetchCampaigns, fetchTemplates])

  // Load analytics when dashboard sub-tab changes
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadAnalyticsData(dashboardSubTab)
    }
  }, [activeTab, dashboardSubTab, loadAnalyticsData])

  // Campaign actions
  const handleCreateCampaign = async () => {
    try {
      const response = await api.createPhishingCampaign(campaignForm)
      if (response.success) {
        toast.success('Campaign created')
        setShowCreateCampaignModal(false)
        resetCampaignForm()
        navigate(`/phishing/campaigns/${response.data.id}`)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create campaign')
    }
  }

  const handleApproveCampaign = async (campaign: PhishingCampaign) => {
    const confirmed = await confirmDialog({
      title: 'Approve Campaign',
      message: `Are you sure you want to approve "${campaign.name}" for sending?`,
      confirmText: 'Approve',
      type: 'warning',
    })
    if (confirmed) {
      try {
        const response = await api.approvePhishingCampaign(campaign.id)
        if (response.success) {
          toast.success('Campaign approved')
          fetchCampaigns()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to approve campaign')
      }
    }
  }

  const handleStartCampaign = async (campaign: PhishingCampaign) => {
    const confirmed = await confirmDialog({
      title: 'Start Campaign',
      message: `Are you sure you want to start sending "${campaign.name}"? This will send phishing emails to all targeted users.`,
      confirmText: 'Start Sending',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.startPhishingCampaign(campaign.id)
        if (response.success) {
          toast.success(`Campaign started! ${response.emails_queued} emails queued.`)
          fetchCampaigns()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to start campaign')
      }
    }
  }

  const handleCancelCampaign = async (campaign: PhishingCampaign) => {
    const confirmed = await confirmDialog({
      title: 'Cancel Campaign',
      message: `Are you sure you want to cancel "${campaign.name}"?`,
      confirmText: 'Cancel Campaign',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.cancelPhishingCampaign(campaign.id)
        if (response.success) {
          toast.success('Campaign cancelled')
          fetchCampaigns()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to cancel campaign')
      }
    }
  }

  // Template actions
  const handleCloneTemplate = async (template: PhishingTemplate) => {
    const name = prompt('Enter name for the cloned template:', `${template.name} (Copy)`)
    if (!name) return
    try {
      const response = await api.clonePhishingTemplate(template.id, name)
      if (response.success) {
        toast.success('Template cloned')
        navigate(`/phishing/templates/${response.data.id}`)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to clone template')
    }
  }

  const handleDeleteTemplate = async (template: PhishingTemplate) => {
    if (template.is_system) {
      toast.error('Cannot delete system templates')
      return
    }
    const confirmed = await confirmDialog({
      title: 'Delete Template',
      message: `Are you sure you want to delete "${template.name}"?`,
      confirmText: 'Delete',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.deletePhishingTemplate(template.id)
        if (response.success) {
          toast.success('Template deleted')
          fetchTemplates()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete template')
      }
    }
  }

  // Sender profile actions
  const handleCreateSender = async () => {
    try {
      const response = await api.createPhishingSenderProfile(senderForm)
      if (response.success) {
        toast.success('Sender profile created')
        setShowCreateSenderModal(false)
        resetSenderForm()
        fetchSenderProfiles()
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create sender profile')
    }
  }

  const handleDeleteSender = async (sender: PhishingSenderProfile) => {
    if (sender.is_default) {
      toast.error('Cannot delete default sender profile')
      return
    }
    const confirmed = await confirmDialog({
      title: 'Delete Sender Profile',
      message: `Are you sure you want to delete "${sender.name}"?`,
      confirmText: 'Delete',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.deletePhishingSenderProfile(sender.id)
        if (response.success) {
          toast.success('Sender profile deleted')
          fetchSenderProfiles()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete sender profile')
      }
    }
  }

  // Reset forms
  const resetCampaignForm = () => {
    setCampaignForm({
      name: '',
      description: '',
      template_id: '',
      target_type: 'all_users',
      track_opens: true,
      track_clicks: true,
      capture_credentials: false,
      requires_approval: true,
    })
  }

  const resetSenderForm = () => {
    setSenderForm({
      name: '',
      from_name: '',
      from_email: '',
      reply_to_email: '',
    })
  }

  // Calculate rates
  const calculateRate = (part: number, total: number) => {
    if (total === 0) return '0.0'
    return ((part / total) * 100).toFixed(1)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Sneak Preview Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-4 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <Sparkles className="h-6 w-6" />
          <div>
            <h2 className="font-semibold text-lg">Sneak Preview</h2>
            <p className="text-purple-100 text-sm">This module is coming soon. The interface below shows planned functionality.</p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Phishing Simulations</h1>
          <p className="text-gray-500 mt-1">Test and improve employee security awareness</p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'campaigns' && (
            <button
              onClick={() => setShowCreateCampaignModal(true)}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </button>
          )}
          {activeTab === 'templates' && (
            <Link to="/phishing/templates/new" className="btn btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Link>
          )}
          {activeTab === 'senders' && (
            <button
              onClick={() => {
                resetSenderForm()
                setShowCreateSenderModal(true)
              }}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Sender Profile
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'campaigns', label: 'Campaigns', icon: Send },
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'senders', label: 'Sender Profiles', icon: Mail },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboardStats && (
        <div className="space-y-6">
          {/* Dashboard Sub-Tabs & Export Buttons */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'trends', label: 'Trends', icon: TrendingUp },
                { id: 'templates', label: 'Template Effectiveness', icon: Target },
                { id: 'departments', label: 'By Department', icon: Building2 },
                { id: 'offenders', label: 'Repeat Offenders', icon: UserX },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setDashboardSubTab(tab.id as DashboardSubTab)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    dashboardSubTab === tab.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAIModal(true)}
                className="btn btn-secondary"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                AI Tools
              </button>
              <button
                onClick={() => handleExportReport('results')}
                disabled={exportLoading}
                className="btn btn-secondary"
              >
                {exportLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export
              </button>
            </div>
          </div>

          {/* Stats Cards - Always Visible */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">Emails Sent</span>
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-2xl font-bold mt-2">{dashboardStats.total_emails_sent.toLocaleString()}</p>
            </div>
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-blue-700 text-sm">Click Rate</span>
                <MousePointerClick className="h-5 w-5 text-blue-500" />
              </div>
              <p className="text-2xl font-bold text-blue-700 mt-2">
                {dashboardStats.overall_click_rate.toFixed(1)}%
              </p>
            </div>
            <div className="bg-red-50 rounded-lg border border-red-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-red-700 text-sm">Compromise Rate</span>
                <Lock className="h-5 w-5 text-red-500" />
              </div>
              <p className="text-2xl font-bold text-red-700 mt-2">
                {dashboardStats.overall_compromise_rate.toFixed(1)}%
              </p>
            </div>
            <div className="bg-green-50 rounded-lg border border-green-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-green-700 text-sm">Report Rate</span>
                <Flag className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-green-700 mt-2">
                {dashboardStats.overall_report_rate.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Loading State */}
          {analyticsLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          )}

          {/* Overview Sub-Tab */}
          {dashboardSubTab === 'overview' && !analyticsLoading && (
            <>
              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* By Difficulty */}
                <div className="bg-white rounded-lg border p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Click Rate by Difficulty</h3>
                  {dashboardStats.by_difficulty.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dashboardStats.by_difficulty}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="difficulty" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                          <Bar dataKey="click_rate" fill="#ef4444" name="Click Rate" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      No data available
                    </div>
                  )}
                </div>

                {/* Trend Over Time */}
                <div className="bg-white rounded-lg border p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Click Rate Trend</h3>
                  {dashboardStats.trend.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={dashboardStats.trend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                          <Line
                            type="monotone"
                            dataKey="click_rate"
                            stroke="#ef4444"
                            strokeWidth={2}
                            name="Click Rate"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      No trend data available
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Results */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Activity</h3>
                {dashboardStats.recent_results.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardStats.recent_results.map((result, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {result.outcome === 'safe' && <Shield className="h-5 w-5 text-green-500" />}
                          {result.outcome === 'clicked' && <MousePointerClick className="h-5 w-5 text-orange-500" />}
                          {result.outcome === 'compromised' && <AlertTriangle className="h-5 w-5 text-red-500" />}
                          {result.outcome === 'reported' && <Flag className="h-5 w-5 text-blue-500" />}
                          <div>
                            <p className="font-medium text-gray-900">{result.user_email}</p>
                            <p className="text-sm text-gray-500">{result.campaign_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            result.outcome === 'safe' ? 'bg-green-100 text-green-700' :
                            result.outcome === 'clicked' ? 'bg-orange-100 text-orange-700' :
                            result.outcome === 'compromised' ? 'bg-red-100 text-red-700' :
                            result.outcome === 'reported' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {result.outcome}
                          </span>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(result.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Fish className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No recent activity</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Trends Sub-Tab */}
          {dashboardSubTab === 'trends' && !analyticsLoading && (
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-gray-900">Phishing Trends (Last 6 Months)</h3>
                <button onClick={() => fetchPhishingTrends()} className="btn btn-sm btn-secondary">
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
              {phishingTrends.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={phishingTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="emails_sent" stroke="#6366f1" strokeWidth={2} name="Emails Sent" />
                      <Line yAxisId="right" type="monotone" dataKey="click_rate" stroke="#ef4444" strokeWidth={2} name="Click Rate %" />
                      <Line yAxisId="right" type="monotone" dataKey="report_rate" stroke="#22c55e" strokeWidth={2} name="Report Rate %" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-80 flex items-center justify-center text-gray-500">
                  No trend data available
                </div>
              )}
            </div>
          )}

          {/* Template Effectiveness Sub-Tab */}
          {dashboardSubTab === 'templates' && !analyticsLoading && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Most Effective Templates</h3>
                  <button onClick={() => fetchEffectiveTemplates()} className="btn btn-sm btn-secondary">
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Ranked by click rate (higher = more effective at catching users)</p>
              </div>
              {effectiveTemplates.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Difficulty</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaigns</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Click Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Compromise Rate</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {effectiveTemplates.map((template, idx) => (
                      <tr key={template.template_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {idx < 3 && <Award className={`h-4 w-4 ${idx === 0 ? 'text-yellow-500' : idx === 1 ? 'text-gray-400' : 'text-orange-400'}`} />}
                            <span className="font-medium text-gray-900">{template.name}</span>
                          </div>
                          <span className="text-xs text-gray-500">{template.category}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${difficultyColors[template.difficulty]}`}>
                            {template.difficulty}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{template.campaigns_used}</td>
                        <td className="px-6 py-4 text-gray-600">{template.total_sent}</td>
                        <td className="px-6 py-4">
                          <span className="font-semibold text-red-600">{template.click_rate}%</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-red-500">{template.compromise_rate}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  No template effectiveness data available
                </div>
              )}
            </div>
          )}

          {/* Departments Sub-Tab */}
          {dashboardSubTab === 'departments' && !analyticsLoading && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Vulnerability by Department/Role</h3>
                  <button onClick={() => fetchDepartmentVulnerability()} className="btn btn-sm btn-secondary">
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {departmentVulnerability.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                  {departmentVulnerability.map((dept) => (
                    <div key={dept.role} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 capitalize">{dept.role}</h4>
                        <span className="text-sm text-gray-500">{dept.total_targets} tested</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Click Rate</span>
                            <span className={`font-medium ${dept.click_rate > 30 ? 'text-red-600' : dept.click_rate > 15 ? 'text-yellow-600' : 'text-green-600'}`}>
                              {dept.click_rate}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${dept.click_rate > 30 ? 'bg-red-500' : dept.click_rate > 15 ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(dept.click_rate, 100)}%` }}
                            />
                          </div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{dept.clicked} clicked</span>
                          <span className="text-red-500">{dept.compromised} compromised</span>
                          <span className="text-green-500">{dept.reported} reported</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  No department vulnerability data available
                </div>
              )}
            </div>
          )}

          {/* Repeat Offenders Sub-Tab */}
          {dashboardSubTab === 'offenders' && !analyticsLoading && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Repeat Offenders</h3>
                    <p className="text-sm text-gray-500">Users who clicked on 2 or more phishing simulations</p>
                  </div>
                  <button onClick={() => fetchRepeatOffenders()} className="btn btn-sm btn-secondary">
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {repeatOffenders.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaigns</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Times Clicked</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Click Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reported</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {repeatOffenders.map((offender) => (
                      <tr key={offender.user_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {offender.first_name && offender.last_name
                                ? `${offender.first_name} ${offender.last_name}`
                                : offender.email}
                            </p>
                            <p className="text-sm text-gray-500">{offender.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 capitalize">{offender.role || '-'}</td>
                        <td className="px-6 py-4 text-gray-600">{offender.total_campaigns}</td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            {offender.times_clicked}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-semibold text-red-600">{offender.click_rate}%</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-green-600">{offender.times_reported}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  <UserX className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No repeat offenders found</p>
                  <p className="text-sm mt-1">Users who clicked 2+ times will appear here</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={campaignSearch}
                  onChange={(e) => setCampaignSearch(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
              <select
                value={campaignStatusFilter}
                onChange={(e) => setCampaignStatusFilter(e.target.value as PhishingCampaignStatus | '')}
                className="input w-48"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Campaigns List */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opened</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicked</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <Link
                          to={`/phishing/campaigns/${campaign.id}`}
                          className="font-medium text-gray-900 hover:text-primary-600"
                        >
                          {campaign.name}
                        </Link>
                        {campaign.description && (
                          <p className="text-sm text-gray-500 line-clamp-1">{campaign.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[campaign.status]}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {campaign.emails_sent} / {campaign.total_targets}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {campaign.emails_opened} ({calculateRate(campaign.emails_opened, campaign.emails_sent)}%)
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={campaign.links_clicked > 0 ? 'text-orange-600 font-medium' : 'text-gray-600'}>
                        {campaign.links_clicked} ({calculateRate(campaign.links_clicked, campaign.emails_sent)}%)
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={campaign.credentials_submitted > 0 ? 'text-red-600 font-medium' : 'text-gray-600'}>
                        {campaign.credentials_submitted} ({calculateRate(campaign.credentials_submitted, campaign.emails_sent)}%)
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/phishing/campaigns/${campaign.id}`}
                          className="btn btn-sm btn-secondary"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        {campaign.status === 'draft' && campaign.requires_approval && !campaign.approved_at && (
                          <button
                            onClick={() => handleApproveCampaign(campaign)}
                            className="btn btn-sm btn-secondary text-green-600"
                            title="Approve"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </button>
                        )}
                        {campaign.status === 'draft' && (!campaign.requires_approval || campaign.approved_at) && (
                          <button
                            onClick={() => handleStartCampaign(campaign)}
                            className="btn btn-sm btn-primary"
                            title="Start"
                          >
                            <Play className="h-4 w-4" />
                          </button>
                        )}
                        {(campaign.status === 'active' || campaign.status === 'sending') && (
                          <button
                            onClick={() => handleCancelCampaign(campaign)}
                            className="btn btn-sm btn-secondary text-red-600"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {campaigns.length === 0 && (
              <div className="text-center py-12">
                <Fish className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
                <p className="text-gray-500 mb-4">Create a phishing simulation campaign to test your users.</p>
                <button
                  onClick={() => setShowCreateCampaignModal(true)}
                  className="btn btn-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Campaign
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
              <select
                value={templateDifficultyFilter}
                onChange={(e) => setTemplateDifficultyFilter(e.target.value as PhishingDifficulty | '')}
                className="input w-48"
              >
                <option value="">All Difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div key={template.id} className="bg-white rounded-lg border hover:shadow-md transition-shadow">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${difficultyColors[template.difficulty]}`}>
                          {template.difficulty}
                        </span>
                        <span className="text-xs text-gray-500">{template.category}</span>
                        {template.is_system && (
                          <span className="text-xs text-gray-400">(System)</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 mb-3">
                    <p className="font-medium">Subject:</p>
                    <p className="line-clamp-1">{template.subject}</p>
                  </div>

                  <div className="text-xs text-gray-500">
                    <p>From: {template.sender_name} &lt;{template.sender_email}&gt;</p>
                  </div>

                  {template.success_rate !== undefined && (
                    <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
                      <span className="text-gray-500">Click rate:</span>
                      <span className="font-medium text-orange-600">
                        {template.success_rate.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>

                <div className="border-t p-3 flex items-center justify-between">
                  <button
                    onClick={() => setShowTemplatePreview(template)}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Preview
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCloneTemplate(template)}
                      className="btn btn-sm btn-secondary"
                      title="Clone"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {!template.is_system && (
                      <>
                        <Link
                          to={`/phishing/templates/${template.id}`}
                          className="btn btn-sm btn-secondary"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          className="btn btn-sm text-red-600 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {templates.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
              <p className="text-gray-500 mb-4">Create a phishing email template to use in campaigns.</p>
              <Link to="/phishing/templates/new" className="btn btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Sender Profiles Tab */}
      {activeTab === 'senders' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {senderProfiles.map((sender) => (
            <div key={sender.id} className="bg-white rounded-lg border p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{sender.name}</h3>
                  {sender.is_default && (
                    <span className="text-xs text-primary-600">(Default)</span>
                  )}
                </div>
                {!sender.is_default && (
                  <button
                    onClick={() => handleDeleteSender(sender)}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><span className="text-gray-500">From Name:</span> {sender.from_name}</p>
                <p><span className="text-gray-500">From Email:</span> {sender.from_email}</p>
                {sender.reply_to_email && (
                  <p><span className="text-gray-500">Reply-To:</span> {sender.reply_to_email}</p>
                )}
              </div>
              {sender.is_verified && (
                <div className="mt-3 flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle2 className="h-4 w-4" />
                  Verified
                </div>
              )}
            </div>
          ))}

          {senderProfiles.length === 0 && (
            <div className="col-span-full text-center py-12 bg-white rounded-lg border">
              <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No sender profiles</h3>
              <p className="text-gray-500 mb-4">Create a sender profile for your phishing campaigns.</p>
              <button
                onClick={() => {
                  resetSenderForm()
                  setShowCreateSenderModal(true)
                }}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Sender Profile
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateCampaignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Create Phishing Campaign</h2>
              <button
                onClick={() => {
                  setShowCreateCampaignModal(false)
                  resetCampaignForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., Q1 2025 Security Test"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  className="input w-full"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Template <span className="text-red-500">*</span>
                </label>
                <select
                  value={campaignForm.template_id}
                  onChange={(e) => setCampaignForm({ ...campaignForm, template_id: e.target.value })}
                  className="input w-full"
                >
                  <option value="">Select Template</option>
                  {templates.filter(t => t.is_active).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.difficulty})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <select
                  value={campaignForm.target_type}
                  onChange={(e) => setCampaignForm({ ...campaignForm, target_type: e.target.value as any })}
                  className="input w-full"
                >
                  <option value="all_users">All Users</option>
                  <option value="specific_users">Specific Users</option>
                  <option value="by_role">By Role</option>
                  <option value="by_department">By Department</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={campaignForm.track_opens}
                    onChange={(e) => setCampaignForm({ ...campaignForm, track_opens: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Track email opens</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={campaignForm.track_clicks}
                    onChange={(e) => setCampaignForm({ ...campaignForm, track_clicks: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Track link clicks</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={campaignForm.capture_credentials}
                    onChange={(e) => setCampaignForm({ ...campaignForm, capture_credentials: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Capture submitted credentials</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={campaignForm.requires_approval}
                    onChange={(e) => setCampaignForm({ ...campaignForm, requires_approval: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Require admin approval before sending</span>
                </label>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateCampaignModal(false)
                  resetCampaignForm()
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCampaign}
                disabled={!campaignForm.name || !campaignForm.template_id}
                className="btn btn-primary disabled:opacity-50"
              >
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Sender Profile Modal */}
      {showCreateSenderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Create Sender Profile</h2>
              <button
                onClick={() => {
                  setShowCreateSenderModal(false)
                  resetSenderForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profile Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={senderForm.name}
                  onChange={(e) => setSenderForm({ ...senderForm, name: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., IT Support"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={senderForm.from_name}
                  onChange={(e) => setSenderForm({ ...senderForm, from_name: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., IT Support Team"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={senderForm.from_email}
                  onChange={(e) => setSenderForm({ ...senderForm, from_email: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., it-support@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reply-To Email</label>
                <input
                  type="email"
                  value={senderForm.reply_to_email}
                  onChange={(e) => setSenderForm({ ...senderForm, reply_to_email: e.target.value })}
                  className="input w-full"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateSenderModal(false)
                  resetSenderForm()
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSender}
                disabled={!senderForm.name || !senderForm.from_name || !senderForm.from_email}
                className="btn btn-primary disabled:opacity-50"
              >
                Create Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {showTemplatePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Template Preview</h2>
              <button
                onClick={() => setShowTemplatePreview(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">From:</span> {showTemplatePreview.sender_name} &lt;{showTemplatePreview.sender_email}&gt;
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Subject:</span> {showTemplatePreview.subject}
                </p>
              </div>

              <div
                className="prose max-w-none border rounded-lg p-4"
                dangerouslySetInnerHTML={{ __html: showTemplatePreview.body_html }}
              />

              {showTemplatePreview.indicators.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm font-medium text-yellow-800 mb-2">Phishing Indicators:</p>
                  <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                    {showTemplatePreview.indicators.map((indicator, idx) => (
                      <li key={idx}>{indicator}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowTemplatePreview(null)}
                className="btn btn-secondary w-full"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Tools Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">AI Phishing Tools</h2>
                  <p className="text-sm text-gray-500">Generate phishing indicators with AI</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAIModal(false)
                  setGeneratedIndicators([])
                  setAiSubject('')
                  setAiSenderName('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Indicator Suggester */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  Phishing Indicator Suggester
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Enter email details and AI will suggest red flags that users should look for.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject Line <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={aiSubject}
                      onChange={(e) => setAiSubject(e.target.value)}
                      className="input w-full"
                      placeholder="e.g., Urgent: Your account has been compromised"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sender Name (optional)
                    </label>
                    <input
                      type="text"
                      value={aiSenderName}
                      onChange={(e) => setAiSenderName(e.target.value)}
                      className="input w-full"
                      placeholder="e.g., IT Support Team"
                    />
                  </div>
                  <button
                    onClick={handleSuggestIndicators}
                    disabled={aiGenerating || !aiSubject.trim()}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {aiGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Suggest Indicators
                      </>
                    )}
                  </button>
                </div>

                {/* Generated Indicators */}
                {generatedIndicators.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Suggested Indicators ({generatedIndicators.length})
                    </h4>
                    <div className="bg-yellow-50 rounded-lg p-3">
                      <ul className="space-y-2">
                        {generatedIndicators.map((indicator, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-yellow-800">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <span>{indicator}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Copy these indicators to your phishing template to help users learn.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex items-center justify-end">
              <button
                onClick={() => {
                  setShowAIModal(false)
                  setGeneratedIndicators([])
                  setAiSubject('')
                  setAiSenderName('')
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
